import type { ActionContext } from 'vuex'
import type { RootState } from '@/store'
import { CONSENT_TEXT_KIND } from '@/constants/consent-texts'
import {
  consentTextBody,
  consentTextEntry,
  consentTextStatus,
  consentVersionsStale,
  loadConsentTexts,
} from '@/helpers/consent/texts'

type Entry = {
  kind: string
  status: string
  version: string
  locale: string | null
  body: any
  publishedAt: string | null
}

type ByKind = { [kind: string]: Entry }

// A reactive mirror of the snapshot in helpers/consent/texts.js, committed once after the fetch
// resolves and never mutated again. Safe because a locale or country change reloads the page, so
// the snapshot cannot go stale mid-session. Non-Vue code reads the helper directly; this exists
// only so components re-render when the texts land.
interface State {
  byKind: ByKind
  loaded: boolean
  failed: boolean
}

const state: State = {
  byKind: {},
  loaded: false,
  failed: false,
}

const getters = {
  consentTextsLoaded: (state: State) => state.loaded,
  consentTextsFailed: (state: State) => state.failed,
  consentVersionsStale: () => consentVersionsStale(),

  // 'published' | 'invalid' | 'unpublished' | 'unavailable' - see helpers/consent/texts.js for
  // why a body we could not read is not the same thing as a kind nobody has published.
  consentTextStatus: () => (kind: string) => consentTextStatus(kind),

  // The language the revision is actually written in, which is the Czech original for every
  // locale outside cz/en/ru. Exposed, not displayed: marking it is a design decision.
  consentTextLocale: (state: State) => (kind: string) => {
    const entry = consentTextEntry(kind) as Entry | null
    return entry ? entry.locale : null
  },

  // { title, channels: { email, sms, messenger } }
  checkoutMarketingText: (state: State) =>
    state.loaded ? consentTextBody(CONSENT_TEXT_KIND.CHECKOUT_MARKETING) : null,

  // Null until the kind is published. There is no local fallback for this one and there must not
  // be: the checkout shows nothing without a published text, so it never has a version to guess.
  checkoutMarketingVersion: (state: State) => {
    const entry = state.loaded
      ? (consentTextEntry(CONSENT_TEXT_KIND.CHECKOUT_MARKETING) as Entry | null)
      : null
    return entry ? entry.version : null
  },

  // [{ name, vendor, category, purpose, retention }]. `purpose` and `retention` arrive as
  // finished strings, so the Czech pluralisation tools/consent-policy-table.js does by hand is
  // the backend's job here.
  cookieListRows: (state: State) =>
    state.loaded ? consentTextBody(CONSENT_TEXT_KIND.COOKIE_LIST) : null,

  cookieBannerHtml: (state: State) =>
    state.loaded ? consentTextBody(CONSENT_TEXT_KIND.COOKIE_BANNER) : null,

  privacyPolicyHtml: (state: State) =>
    state.loaded ? consentTextBody(CONSENT_TEXT_KIND.PRIVACY_POLICY) : null,
}

const actions = {
  // Never rejects: a caller that cannot load the texts carries on with the ones that shipped.
  LOAD_CONSENT_TEXTS: async (
    { commit }: ActionContext<State, RootState>,
    payload: { timeout?: number } = {},
  ) => {
    const result = await loadConsentTexts({ timeout: payload.timeout })

    commit('SET_CONSENT_TEXTS', { byKind: result.byKind, failed: !result.ok })

    return result
  },
}

const mutations = {
  SET_CONSENT_TEXTS(state: State, payload: { byKind: ByKind; failed: boolean }) {
    state.byKind = payload.byKind
    state.loaded = true
    state.failed = payload.failed
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
